import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, BarChart3, Shield, Users, Zap, Loader2, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

export function PresentationDownload() {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloaded(false);
    try {
      const response = await api.get('/admin/presentation/download', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Insyra_Platform_Overview_${new Date().toISOString().split('T')[0]}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloaded(true);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  const slides = [
    { icon: FileText, title: 'What is Insyra', desc: 'Platform overview with live call count and team size', color: 'text-purple-600 bg-purple-50' },
    { icon: Zap, title: 'How It Works', desc: '4-step pipeline: Sync → Transcribe → Analyze → Report + performance stats', color: 'text-blue-600 bg-blue-50' },
    { icon: Users, title: 'User Benefits', desc: 'Instant feedback, skill gap detection, leaderboards, training integration', color: 'text-green-600 bg-green-50' },
    { icon: Shield, title: 'Compliance & QA', desc: 'Fatal parameters, compliance rate, violation tracking — with real numbers', color: 'text-red-600 bg-red-50' },
    { icon: BarChart3, title: 'Impact & Results', desc: '100% call coverage, <5 min feedback, 0 manual QA hours needed', color: 'text-amber-600 bg-amber-50' },
  ];

  return (
    <div className="space-y-6" data-testid="presentation-download">
      {/* Hero section */}
      <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl p-6 border border-indigo-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Insyra Platform Overview Deck</h3>
            <p className="text-sm text-slate-600 mt-1">
              Professional 5-slide presentation with live platform metrics.
              Perfect for team onboarding, stakeholder walkthroughs, and compliance reviews.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-700">PPTX Format</Badge>
              <Badge variant="outline" className="text-[10px] border-green-200 text-green-700">Live Data</Badge>
              <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700">5 Slides</Badge>
            </div>
          </div>
          <Button 
            onClick={handleDownload}
            disabled={downloading}
            className="bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200 px-6"
            size="lg"
            data-testid="download-pptx-btn"
          >
            {downloading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : downloaded ? (
              <CheckCircle2 className="h-5 w-5 mr-2" />
            ) : (
              <Download className="h-5 w-5 mr-2" />
            )}
            {downloading ? 'Generating...' : downloaded ? 'Downloaded!' : 'Download Presentation'}
          </Button>
        </div>
      </div>

      {/* Slide Preview */}
      <div>
        <h4 className="text-sm font-medium text-slate-700 mb-3">Slide Contents</h4>
        <div className="grid gap-2">
          {slides.map((slide, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-slate-50 transition-colors">
              <div className={`p-2 rounded-lg ${slide.color.split(' ')[1]}`}>
                <slide.icon className={`h-4 w-4 ${slide.color.split(' ')[0]}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-muted-foreground">Slide {i + 1}</span>
                  <span className="font-medium text-sm text-slate-800">{slide.title}</span>
                </div>
                <p className="text-xs text-slate-500 truncate">{slide.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <p className="text-xs text-muted-foreground border-t pt-3">
        The presentation is generated with your current platform data (total calls processed, compliance rates, team size, average scores).
        Download a fresh copy anytime to get the latest numbers.
      </p>
    </div>
  );
}
